<template>
  <div
    class="changelog-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="changelog-title"
    tabindex="-1"
    @keydown="handleKeydown"
  >
    <div class="changelog-modal__backdrop" @click="dismiss"></div>
    <section ref="panel" class="changelog-modal__panel" tabindex="-1" @click.stop>
      <header class="changelog-modal__header">
        <div>
          <p class="changelog-modal__kicker">Neuigkeiten</p>
          <h2 id="changelog-title" class="changelog-modal__title">
            Versionshinweise {{ version }}
          </h2>
        </div>
        <button
          ref="closeButton"
          type="button"
          class="changelog-modal__close"
          aria-label="Versionshinweise schließen"
          @click="dismiss"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div class="changelog-modal__body" v-html="safeMarkdown"></div>

      <footer class="changelog-modal__footer">
        <button type="button" class="changelog-modal__dismiss" @click="dismiss">
          Schließen
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import DOMPurify from "dompurify";
import { marked } from "marked";

export default {
  name: "ChangelogModal",
  props: {
    version: {
      type: String,
      default: "",
    },
    markdown: {
      type: String,
      default: "",
    },
  },
  emits: ["dismiss"],
  computed: {
    safeMarkdown() {
      return DOMPurify.sanitize(marked.parse(this.markdown || ""));
    },
  },
  data() {
    return {
      previousActiveElement: null,
      previousBodyOverflow: "",
    };
  },
  mounted() {
    this.previousActiveElement = document.activeElement;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.$nextTick(() => this.$refs.closeButton?.focus());
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
    if (this.previousActiveElement?.isConnected) {
      this.previousActiveElement.focus();
    }
  },
  methods: {
    dismiss() {
      this.$emit("dismiss");
    },
    focusableElements() {
      return Array.from(
        this.$refs.panel?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.dismiss();
        return;
      }
      if (event.key !== "Tab") return;

      const elements = this.focusableElements();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.changelog-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.changelog-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.58);
  cursor: pointer;
}

.changelog-modal__panel {
  position: relative;
  display: flex;
  width: min(46rem, 100%);
  max-height: min(42rem, calc(100vh - 2rem));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 1.5rem 4rem rgba(17, 24, 39, 0.3);
}

.changelog-modal__header,
.changelog-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.25rem;
}

.changelog-modal__footer {
  justify-content: flex-end;
  border-top: 1px solid #e5e7eb;
  border-bottom: 0;
}

.changelog-modal__kicker {
  margin: 0;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.changelog-modal__title {
  margin: 0.2rem 0 0;
  font-size: 1.35rem;
  font-weight: 800;
}

.changelog-modal__close {
  min-width: 2.25rem;
  min-height: 2.25rem;
  border: 0;
  padding: 0;
  font-size: 1.8rem;
  line-height: 1;
}

.changelog-modal__body {
  min-height: 0;
  overflow-y: auto;
  padding: 1.25rem;
}

.changelog-modal__body :deep(h1),
.changelog-modal__body :deep(h2),
.changelog-modal__body :deep(h3) {
  margin: 0 0 0.65rem;
  font-weight: 800;
  line-height: 1.2;
}

.changelog-modal__body :deep(h1) {
  font-size: 1.35rem;
}

.changelog-modal__body :deep(h2) {
  margin-top: 1rem;
  font-size: 1.1rem;
}

.changelog-modal__body :deep(h1:first-child),
.changelog-modal__body :deep(h2:first-child) {
  margin-top: 0;
}

.changelog-modal__body :deep(p),
.changelog-modal__body :deep(ul),
.changelog-modal__body :deep(ol) {
  margin: 0 0 0.75rem;
}

.changelog-modal__body :deep(ul) {
  list-style-type: disc;
  padding-left: 1.4rem;
}

.changelog-modal__body :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.4rem;
}

.changelog-modal__dismiss {
  background: #87cee0;
  color: #fff;
}

@media (max-width: 640px) {
  .changelog-modal {
    padding: 0.5rem;
  }

  .changelog-modal__panel {
    max-height: calc(100vh - 1rem);
  }
}
</style>
